import { useEffect, useRef, useState } from "react";
import { sendFirebasePhoneCode, resetFirebaseRecaptcha, formatFirebasePhoneError } from "../lib/firebasePhone";

export default function SignupPhoneVerification({ phone, proof, onVerified }) {
  const [confirmation, setConfirmation] = useState(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [resendAt, setResendAt] = useState(0);
  const [now, setNow] = useState(Date.now());
  const generation = useRef(0);
  useEffect(() => {
    generation.current += 1;
    setConfirmation(null); setCode(""); setError(""); setBusy(false);
    resetFirebaseRecaptcha();
    return () => { generation.current += 1; resetFirebaseRecaptcha(); };
  }, [phone]);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    if (proof && now - proof.at > 9 * 60 * 1000) {
      onVerified(null); setError("Phone verification expired. Send a new SMS code.");
    }
  }, [proof, now, onVerified]);
  const send = async () => {
    if (phone.length !== 10 || busy || Date.now() < resendAt) return;
    const attempt = ++generation.current;
    setBusy(true); setError(""); onVerified(null);
    try {
      resetFirebaseRecaptcha();
      const response = await sendFirebasePhoneCode(`+1${phone}`, "signup-phone-recaptcha");
      if (attempt !== generation.current) return;
      setConfirmation(response); setCode(""); setResendAt(Date.now() + 60000);
    } catch (e) {
      if (attempt === generation.current) setError(formatFirebasePhoneError(e));
    } finally { if (attempt === generation.current) setBusy(false); }
  };
  const verify = async () => {
    if (!confirmation || code.length !== 6 || busy) return;
    const attempt = ++generation.current;
    setBusy(true); setError("");
    try {
      const credential = await confirmation.confirm(code);
      const token = await credential.user.getIdToken(true);
      if (attempt !== generation.current) return;
      onVerified({ phone, token, at: Date.now() }); setCode("");
    } catch (e) {
      if (attempt === generation.current) setError(formatFirebasePhoneError(e));
    } finally { if (attempt === generation.current) setBusy(false); }
  };
  return <section className="rounded-xl border border-slate-200 p-4 space-y-3" aria-label="Verify signup phone">
    <div id="signup-phone-recaptcha" />
    {proof?.phone === phone ? <p role="status" className="text-sm text-green-700">Phone verified. Continue below to create your account.</p> : <>
      <p className="text-sm text-slate-600">Verify your US phone by SMS. Your account cannot be created until phone verification is complete.</p>
      <button type="button" data-testid="signup-send-sms" onClick={send} disabled={phone.length !== 10 || busy || now < resendAt}
        className="rounded-full border border-[#2F6FA5] px-4 py-2 text-[#2F6FA5] disabled:opacity-50">
        {busy ? "Please wait..." : now < resendAt ? `Resend in ${Math.ceil((resendAt-now)/1000)}s` : confirmation ? "Resend SMS code" : "Send SMS code"}
      </button>
      {confirmation && <div className="space-y-2">
        <label className="block text-sm" htmlFor="signup-sms-code">SMS code sent to +1 {phone}</label>
        <input id="signup-sms-code" data-testid="signup-sms-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code}
          onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0,6))} className="w-full rounded-lg border border-slate-300 p-3" />
        <button type="button" data-testid="signup-verify-sms" disabled={busy || code.length !== 6} onClick={verify}
          className="rounded-full bg-[#2F6FA5] px-4 py-2 text-white disabled:opacity-50">{busy ? "Verifying..." : "Verify phone"}</button>
      </div>}
    </>}
    {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
  </section>;
}
